import axios from "axios";
import { useFormik, FormikErrors } from "formik";
import React, { useEffect, useState } from "react";
import { useMutation, useQuery } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import * as Yup from "yup";
import { Button } from "../../components/Button";
import { Input } from "../../components/Input";
import { Label } from "../../components/Label";
import { Loader } from "../../components/Loader";

export const EditCategories: React.FC = () => {
  const { id } = useParams();
  const [preview, setPreview] = useState<string | null>(null);

  const { mutate, isError, isLoading, data } = useMutation(
    async (formData: FormData) => {
      return await axios.patch(`/categories/${id}`, formData);
    }
  );

  const { data: category, isLoading: categoryLoading } = useQuery(
    ["Get Category", id],
    async () => {
      const response = await axios.get(`/categories/${id}`);
      return response.data.category;
    }
  );

  const navigate = useNavigate();
  const formik = useFormik({
    initialValues: {
      names: [
        { language: "ar", name: "" },
        { language: "en", name: "" },
        { language: "ur", name: "" },
      ],
      logo: null as File | null,
    },
    validationSchema: Yup.object({
      names: Yup.array()
        .of(
          Yup.object({
            language: Yup.string().required(),
            name: Yup.string().required("Required"),
          })
        )
        .min(1),
    }),
    onSubmit: async (values) => {
      try {
        const formData = new FormData();
        const names = values.names.filter((n) => n.name.trim() !== "");
        formData.append("names", JSON.stringify(names));

        if (values.logo) {
          formData.append("categoryLogo", values.logo);
        }
        mutate(formData);
      } catch (error) {
        console.log(error);
      }
    },
  });

  useEffect(() => {
    if (category) {
      if (Array.isArray(category.names)) {
        const updatedNames = formik.values.names.map((n) => {
          const match = category.names.find(
            (c: any) => c.language === n.language
          );
          return { ...n, name: match ? match.name : "" };
        });
        formik.setFieldValue("names", updatedNames);
      }

      setPreview(
        category?.logo
          ? process.env.REACT_APP_UPLOAD_BASE_URL + category?.logo
          : null
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category]);

  useEffect(() => {
    if (data && !isError) {
      navigate(`/dashboard/categories`);
    }
  }, [data, isError, navigate]);

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    formik.setFieldValue("logo", file);
    if (file) {
      setPreview(URL.createObjectURL(file));
    }
  };

  if (categoryLoading) {
    return (
      <div className="flex justify-center items-center pt-32">
        <Loader />
      </div>
    );
  }

  return (
    <div className="flex">
      <form
        className="flex w-8/12 flex-col gap-4 "
        onSubmit={formik.handleSubmit}
      >
        <>
          <h3 className="text-3xl font-medium py-5">Edit Category</h3>

          {formik.values.names.map((item, index) => (
            <div className="w-full" key={index}>
              <Label htmlFor={`names.${index}.name`}>
                Name ({item.language.toUpperCase()})
              </Label>
              <Input
                type="text"
                id={`names.${index}.name`}
                className={`w-full ${
                  item.language === "ur" ? "h-full font-urdu" : ""
                }`}
                {...formik.getFieldProps(`names.${index}.name`)}
                error={
                  formik.touched.names?.[index]?.name
                    ? (
                        formik.errors.names?.[index] as FormikErrors<{
                          language: string;
                          name: string;
                        }>
                      )?.name || ""
                    : ""
                }
              />
            </div>
          ))}

          <div className="w-full">
            <Label htmlFor="logo">Logo</Label>
            <input
              type="file"
              id="logo"
              accept="image/*"
              onChange={handleLogoChange}
              className="w-full border px-3 py-2 rounded-md"
            />
            {preview && (
              <div className="mt-2">
                <img
                  src={preview}
                  alt="Logo Preview"
                  className="max-w-20 p-2 object-contain border"
                />
              </div>
            )}
          </div>

          <div className="w-full flex justify-between gap-3 mb-10">
            <Button green className="w-1/2" type="submit" loading={isLoading}>
              Save
            </Button>
            <Button
              type="button"
              lightRed
              className="w-1/2"
              onClick={() => (window.location.href = "/dashboard/categories")}
            >
              Cancel
            </Button>
          </div>
        </>
      </form>
    </div>
  );
};
